import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { db } from "../../utils/firebase"
import { AuthContext } from "../../utils/providers/auth"
import moment from "moment"
import "./bookings-received.scss"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import { LoadingIcon } from "../../utils/svgs"

/**
 * Resolves a mixed Firestore timestamp / millisecond / Date value to milliseconds.
 */
const toMillis = (value) => {
	if (!value) return null
	if (typeof value === "number") return value
	if (value?.seconds) return value.seconds * 1000
	if (value?.toMillis) return value.toMillis()
	if (value instanceof Date) return value.getTime()
	return null
}

/**
 * Inline-loaded client name cell. Mirrors the masking logic used on Assign Bookings.
 */
function ClientCell({ clientID, maskDetails }) {
	const [clientName, setClientName] = useState("")

	useEffect(() => {
		if (!clientID) return
		let cancelled = false

		db.doc(`clients/${clientID}`)
			.get()
			.then((clientDoc) => {
				if (cancelled || !clientDoc.exists) return
				const { first_name, last_name } = clientDoc.data() || {}
				setClientName(`${first_name || ""} ${last_name || ""}`.trim())
			})

		return () => {
			cancelled = true
		}
	}, [clientID])

	if (!clientID) return <small className="no-value">No client</small>

	if (maskDetails) return <>{clientName ? clientName.replace(/./g, "*") : "Loading..."}</>

	return <NavLink to={`/client/${clientID}`}>{clientName || "Loading..."}</NavLink>
}

/**
 * Hidden "bookings received" page for reviewing the history of bookings moved
 * into a sales agent's name. Admins / super admins can pick any agent from the
 * dropdown; everyone else is locked to their own account.
 *
 * Each row represents a single entry from a booking's `agent_history` where
 * the target of the change matches the selected agent. Rows are sorted with
 * the oldest move-in event at the top and the most recent at the bottom.
 */
export default function BookingsReceived() {
	const { user } = useContext(AuthContext)
	const canPickAnyAgent = Boolean(user?.is_admin || user?.is_super_admin)

	// `allUsersByUID` covers every user we've ever stored (including ex-staff),
	// so historical `from` / `changed_by` entries resolve to real names.
	// `agentsByUID` is the current active staff only, used for the dropdown.
	const [allUsersByUID, setAllUsersByUID] = useState({})
	const [agentsByUID, setAgentsByUID] = useState({})
	const [selectedAgent, setSelectedAgent] = useState("")

	const [loading, setLoading] = useState(false)
	const [loaded, setLoaded] = useState(false)
	const [rows, setRows] = useState([])

	const requestIdRef = useRef(0)

	useEffect(() => {
		db.collection("users")
			.orderBy("first_name")
			.get()
			.then((userDocs) => {
				const fullMap = {}
				const staffMap = {}

				userDocs.forEach((userDoc) => {
					const data = userDoc.data() || {}
					const { first_name, last_name, is_staff } = data
					const displayName = `${first_name || ""} ${last_name || ""}`.trim() || userDoc.id

					fullMap[userDoc.id] = displayName

					if (is_staff) {
						staffMap[userDoc.id] = displayName
					}
				})

				setAllUsersByUID(fullMap)
				setAgentsByUID(staffMap)
			})
	}, [])

	useEffect(() => {
		if (!canPickAnyAgent && user?.uid) {
			setSelectedAgent(user.uid)
		}
	}, [canPickAnyAgent, user?.uid])

	const loadMoveInEvents = async (agentUID) => {
		if (!agentUID) return

		const requestId = ++requestIdRef.current
		setLoading(true)
		setLoaded(true)
		setRows([])

		try {
			// `orderBy` on `agent_changed_by` naturally excludes bookings that
			// have never been reassigned (Firestore skips docs missing the
			// ordered field). This keeps the scan proportional to the number
			// of bookings that have moved between agents.
			const snapshot = await db.collection("bookings").orderBy("agent_changed_by").get()

			if (requestIdRef.current !== requestId) return

			const events = []

			snapshot.forEach((doc) => {
				const data = doc.data() || {}
				const history = Array.isArray(data.agent_history) ? data.agent_history : []
				if (history.length === 0) return

				history.forEach((entry, index) => {
					if (!entry || entry.to !== agentUID) return
					const changedAtMs = toMillis(entry.changed_at)
					const margins = data.margins || {}

					events.push({
						key: `${doc.id}-${index}-${changedAtMs || "na"}`,
						bookingID: doc.id,
						reference: data.reference || "",
						clientID: data.client || "",
						currentAgent: data.agent || "",
						originalAgent: data.original_agent || "",
						checkInMs: toMillis(data.check_in),
						holidayCost: Number(margins.customer_price) || 0,
						salesMargin: Number(margins.profit) || 0,
						from: entry.from || "",
						to: entry.to,
						changedBy: entry.changed_by || "",
						changedAtMs,
					})
				})
			})

			events.sort((a, b) => {
				const aMs = a.changedAtMs ?? 0
				const bMs = b.changedAtMs ?? 0
				return aMs - bMs
			})

			if (requestIdRef.current === requestId) setRows(events)
		} catch (error) {
			console.error("Error loading agent move-in history:", error)
		} finally {
			if (requestIdRef.current === requestId) setLoading(false)
		}
	}

	useEffect(() => {
		if (selectedAgent) loadMoveInEvents(selectedAgent)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAgent])

	const nameForUID = (uid) => {
		if (!uid) return ""
		return allUsersByUID[uid] || agentsByUID[uid] || uid
	}

	const selectedAgentName = useMemo(() => nameForUID(selectedAgent), [selectedAgent, allUsersByUID, agentsByUID])

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Bookings received</h1>
			</Title>

			<div className="page-filters bookings-received-filters">
				{canPickAnyAgent ? (
					<Select
						label="Sales agent:"
						placeholder="Sales agent:"
						value={agentsByUID[selectedAgent]}
						activeOnHover={true}
						onSelect={(option) => setSelectedAgent(option.option)}
						options={agentsByUID}
					/>
				) : (
					<div className="locked-agent">
						<span className="locked-agent-label">Sales agent:</span>
						<strong>{selectedAgentName || "You"}</strong>
					</div>
				)}
			</div>

			<Table
				className="bookings-received-table"
				headings={["Moved in on", "Booking ref", "Client", "Travel date", "Holiday cost", "Sales margin", "From", "Moved by", "Currently owned by", ""]}
				noResults={loaded && !loading && rows.length === 0}
				noResultsMessage={
					selectedAgent
						? `No bookings have been moved into ${selectedAgentName || "this agent"}'s name yet.`
						: "Pick a sales agent to see the bookings moved into their name."
				}>
				{rows.map((row) => (
					<tr key={row.key}>
						<td>{row.changedAtMs ? moment(row.changedAtMs).format("MMM D[,] YYYY [at] HH:mm") : <small className="no-value">Unknown</small>}</td>
						<td>
							{row.reference ? (
								<NavLink to={`/booking/${row.bookingID}`}>{row.reference}</NavLink>
							) : (
								<NavLink to={`/booking/${row.bookingID}`}>
									<small className="no-value">No reference</small>
								</NavLink>
							)}
						</td>
						<td>
							<ClientCell
								clientID={row.clientID}
								maskDetails={user?.mask_details}
							/>
						</td>
						<td>{row.checkInMs ? moment(row.checkInMs).format("MMM D[,] YYYY") : <small className="no-value">Not set</small>}</td>
						<td>£{row.holidayCost.toLocaleString()}</td>
						<td>£{row.salesMargin.toLocaleString()}</td>
						<td>{row.from ? nameForUID(row.from) : <small className="no-value">Unknown</small>}</td>
						<td>{row.changedBy ? nameForUID(row.changedBy) : <small className="no-value">Unknown</small>}</td>
						<td>
							{row.currentAgent === selectedAgent ? (
								<span className="still-owner">Still with {selectedAgentName || "this agent"}</span>
							) : (
								nameForUID(row.currentAgent) || <small className="no-value">Unknown</small>
							)}
						</td>
						<td className="is-button">
							<NavLink to={`/booking/${row.bookingID}`}>View booking</NavLink>
							<a
								target="_blank"
								rel="noreferrer"
								href={`/booking/${row.bookingID}`}>
								View in new tab
							</a>
						</td>
					</tr>
				))}

				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}
			</Table>
		</Tile>
	)
}
